/**
 * @voltxampere/engine — Assertion batch runner (Sprint 9)
 *
 * `runAssertions` is the thin glue the DC / transient solvers
 * call once they have a converged result. It:
 *
 *   1. Gathers assertions from both sources — the Circuit-level
 *      `Assertion[]` (with id/locale/enabled/description) AND the
 *      per-analysis settings-level `string[]` (simple DSL source).
 *   2. For each, parses → evaluates against the snapshot with
 *      proper resolution pools for "did you mean" suggestions.
 *   3. Parse or evaluation errors become a failed AssertionResult
 *      with the error surfaced in `message` — never throw out of
 *      the solver path, so a malformed user assertion cannot
 *      break the analysis.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { evaluateAssertion } from "./evaluate.js";
import { AssertionParseError, parseAssertion } from "./parser.js";
/** Run every assertion attached to this circuit for this analysis. */
export function runAssertions(ctx) {
    const { circuit, snapshot, analysisKind } = ctx;
    const results = [];
    const knownNodes = circuit.nets.map((n) => n.name);
    const knownComponents = circuit.components.map((c) => c.id);
    const nodeNameToId = new Map();
    for (const n of circuit.nets)
        nodeNameToId.set(n.name, n.id);
    const settingsSources = ctx.extraSources ??
        (analysisKind === "DC"
            ? (circuit.simulationSettings.dc.assertions ?? [])
            : analysisKind === "TRANSIENT"
                ? (circuit.simulationSettings.transient.assertions ?? [])
                : []);
    let anonymousIdx = 0;
    for (const src of settingsSources) {
        anonymousIdx += 1;
        results.push(runOne({
            id: `settings-${analysisKind.toLowerCase()}-${String(anonymousIdx)}`,
            expression: src,
            locale: "en",
            enabled: true,
        }, snapshot, analysisKind, knownNodes, knownComponents, nodeNameToId));
    }
    for (const a of circuit.assertions) {
        if (!a.enabled)
            continue;
        results.push(runOne(a, snapshot, analysisKind, knownNodes, knownComponents, nodeNameToId));
    }
    return results;
}
/** Shortcut for DC result consumers. */
export function runAssertionsOnDC(circuit, dc) {
    return runAssertions({ circuit, snapshot: { dc }, analysisKind: "DC" });
}
/** Shortcut for transient result consumers. */
export function runAssertionsOnTransient(circuit, transient) {
    return runAssertions({ circuit, snapshot: { transient }, analysisKind: "TRANSIENT" });
}
function runOne(a, snapshot, analysisKind, knownNodes, knownComponents, nodeNameToId) {
    try {
        const ast = parseAssertion(a.expression, { locale: a.locale });
        return evaluateAssertion(ast, snapshot, analysisKind, {
            assertionId: a.id,
            sourceText: a.expression,
            locale: a.locale,
            knownNodes,
            knownComponents,
            nodeNameToId,
        });
    }
    catch (err) {
        const message = err instanceof AssertionParseError
            ? err.message
            : err instanceof Error
                ? err.message
                : String(err);
        return {
            assertionId: a.id,
            sourceText: a.expression,
            passed: false,
            skipped: false,
            message: `Parse error: ${message}`,
            actual: "parse-error",
            expected: "valid DSL",
            margin: NaN,
            marginUnit: "",
            analysisKind,
        };
    }
}
//# sourceMappingURL=run.js.map